export const SOCIAL_PLATFORMS = [
  'instagram',
  'tiktok',
  'facebook',
  'x',
  'linkedin',
  'website'
] as const;

export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];
export type SocialLinks = Partial<Record<SocialPlatform, string>>;

export function parseSocialLinks(raw: string | null | undefined): SocialLinks {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    const links: SocialLinks = {};
    for (const platform of SOCIAL_PLATFORMS) {
      const value = (parsed as Record<string, unknown>)[platform];
      if (typeof value === 'string' && value.trim()) {
        links[platform] = value.trim();
      }
    }
    return links;
  } catch {
    return {};
  }
}

/**
 * Accepts a full http(s) URL or a bare host/path (`fb.com/oleg`, `oleg.com`)
 * and returns an https URL. Leaves other schemes (`javascript:`) untouched so
 * validation can reject them.
 */
function hasExplicitScheme(value: string): boolean {
  return /^[a-z][a-z0-9+.-]*:/i.test(value);
}

export function normalizeOptionalHttpUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';

  if (hasExplicitScheme(trimmed) && !/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  const candidate = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed.replace(/^\/+/, '')}`;

  try {
    const url = new URL(candidate);
    if (!url.hostname) return '';
    return candidate;
  } catch {
    return '';
  }
}

export function emptySocialLinks(): Record<SocialPlatform, string> {
  return {
    instagram: '',
    tiktok: '',
    facebook: '',
    x: '',
    linkedin: '',
    website: ''
  };
}

export const MAX_SOCIAL_LINK_LENGTH = 300;

const SOCIAL_LINK_PATTERN = /^https?:\/\/\S+$/i;

export function isAcceptableSocialLink(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed && /\s/.test(trimmed)) return false;
  const normalized = normalizeOptionalHttpUrl(value);
  return (
    normalized === '' ||
    (normalized.length <= MAX_SOCIAL_LINK_LENGTH &&
      SOCIAL_LINK_PATTERN.test(normalized))
  );
}

/** Host/path shown in the profile editor before the editable handle. */
export const SOCIAL_LINK_PREFIX: Record<SocialPlatform, string | null> = {
  instagram: 'instagram.com/',
  tiktok: 'tiktok.com/@',
  facebook: 'facebook.com/',
  x: 'x.com/',
  linkedin: 'linkedin.com/in/',
  website: null
};

const PLATFORM_HOST_ALIASES: Record<
  Exclude<SocialPlatform, 'website'>,
  RegExp
> = {
  instagram: /^instagram\.com\/?/i,
  tiktok: /^tiktok\.com\/@?/i,
  facebook: /^(?:facebook|fb)\.com\/?/i,
  x: /^(?:x|twitter)\.com\/?/i,
  linkedin: /^linkedin\.com\/(?:in\/)?/i
};

function stripSchemeAndWww(value: string): string {
  return value
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/+$/, '');
}

export function socialLinkToHandle(
  platform: SocialPlatform,
  stored: string
): string {
  const trimmed = stored.trim();
  if (!trimmed || trimmed === 'https://' || trimmed === 'http://') {
    return '';
  }

  if (platform === 'website') {
    return stripSchemeAndWww(trimmed);
  }

  const prefix = SOCIAL_LINK_PREFIX[platform];
  if (!prefix) return trimmed;

  let rest = stripSchemeAndWww(trimmed);
  const hostPattern = PLATFORM_HOST_ALIASES[platform];
  if (hostPattern.test(rest)) {
    rest = rest.replace(hostPattern, '');
  } else if (rest.includes('/')) {
    return rest;
  }

  return rest.replace(/^@/, '').replace(/\/+$/, '');
}

export function handleToSocialLink(
  platform: SocialPlatform,
  handle: string
): string {
  const trimmed = handle.trim();
  if (!trimmed) return '';

  if (platform === 'website') {
    return trimmed;
  }

  const prefix = SOCIAL_LINK_PREFIX[platform];
  if (!prefix) return trimmed;

  const bare = trimmed.replace(/^@/, '').replace(/^\/+/, '');
  return `${prefix}${bare}`;
}

/** Read-mode label: drop scheme and www. for URLs; leave phone/email as-is. */
export function formatContactLinkDisplay(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  if (!/^https?:\/\//i.test(trimmed) && !trimmed.includes('.')) {
    return trimmed;
  }
  const withoutScheme = stripSchemeAndWww(trimmed);
  return withoutScheme || trimmed;
}
